
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { StaffCategory } from "@/types";

type EmployeeFormProps = {
  onClose: () => void;
};

export function EmployeeForm({ onClose }: EmployeeFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    category: "TEACHING_STAFF" as StaffCategory,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.schoolId) {
      toast({
        title: "Error",
        description: "School ID not found. Please try logging in again.",
        variant: "destructive",
      });
      return;
    }

    console.log("Submitting employee data:", {
      name: formData.name,
      category: formData.category,
      school_id: user.schoolId
    });

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase
        .from("community_members")
        .insert({
          name: formData.name,
          category: formData.category,
          school_id: user.schoolId,
        })
        .select()
        .single();

      if (error) {
        console.error('Error details:', error);
        throw error;
      }

      console.log("Employee added successfully:", data);

      toast({
        title: "Success",
        description: "Employee added successfully",
      });
      onClose();
    } catch (error) {
      console.error('Detailed error adding employee:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add employee",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Employee</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Category</Label>
            <RadioGroup
              value={formData.category}
              onValueChange={(value: StaffCategory) => 
                setFormData(prev => ({ ...prev, category: value }))
              }
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="TEACHING_STAFF" id="teaching" />
                <Label htmlFor="teaching">Teaching Staff</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="NON_TEACHING_STAFF" id="non-teaching" />
                <Label htmlFor="non-teaching">Non Teaching Staff</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="ADMIN" id="admin" />
                <Label htmlFor="admin">Admin</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Adding..." : "Add Employee"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
